import { useEffect, useReducer } from "react"
import { DOCUMENTOS_BUCKET } from "src/settings"
import { formatDateDataGrid } from "src/utils/format-date"
import React from "react"
import {
    DeleteButton,
    RadioButtonTable,
    FileNameColumn,
} from "../../components/TableButtonsDocument"

const AdminColumn = (onDelete, onChangeRadio, onClickIcon) => [
    {
        headerName: "Documento",
        field: "Arquivo",
        flex: true,
        minWidth: 250,
        renderCell: (params) => (
            <FileNameColumn
                onClick={() => onClickIcon(params.row?.ID, params.row?.Arquivo)}
            >
                {params.value}
            </FileNameColumn>
        ),
    },
    {
        headerName: "Tipo de Acesso",
        field: "TipoAcesso",
        flex: true,
        minWidth: 250,
        maxWidth: 250,
        renderCell: (params) => (
            <RadioButtonTable
                defaultRadioValue={params.value}
                onChange={() =>
                    onChangeRadio(params.row?.ID, params.row?.TipoAcesso)
                }
                radioButtonItems={[
                    { value: "PUBLIC", label: "Público" },
                    { value: "PRIVATE", label: "Privado" },
                ]}
            />
        ),
    },
    {
        headerName: "Criado Em",
        field: "CriadoEm",
        flex: true,
        minWidth: 200,
        maxWidth: 200,
        renderCell: (params) => formatDateDataGrid(params),
    },
    {
        headerName: "Ações",
        field: "Excluir",
        minWidth: 100,
        maxWidth: 150,
        flex: true,
        renderCell: (params) => (
            <DeleteButton onClick={() => onDelete(params.row?.Arquivo)} />
        ),
    },
]

const commonColumn = (onClickIcon) => [
    {
        headerName: "Documento",
        field: "Arquivo",
        flex: true,
        minWidth: 250,
        renderCell: (params) => (
            <FileNameColumn
                onClick={() => onClickIcon(params.row?.ID, params.row?.Arquivo)}
            >
                {params.value}
            </FileNameColumn>
        ),
    },
    {
        headerName: "Criado Em",
        field: "CriadoEm",
        flex: true,
        minWidth: 200,
        maxWidth: 200,
        renderCell: (params) => formatDateDataGrid(params),
    },
]

const initialState = {
    loading: false,
    rows: [],
    error: {},
    columns: [],
    modalDelete: {
        isOpen: false,
        onConfirm: () => {},
        onExit: () => {},
    },
}

function reducer(state, action) {
    switch (action.type) {
        case "SET_LOADING":
            return { ...state, loading: action.payload }
        case "SET_ERROR":
            return { ...state, rows: [], error: action.payload }
        case "SET_ROWS":
            return { ...state, rows: action.payload }
        case "SET_MODAL_DELETE":
            return { ...state, modalDelete: action.payload }

        case "RESET_MODAL_DELETE":
            return {
                ...state,
                modalDelete: {
                    isOpen: false,
                    onConfirm: () => {},
                    onExit: () => {},
                },
            }
        case "SET_COLUMNS":
            return {
                ...state,
                columns: action.payload,
            }
        default:
            return state
    }
}

export const useDocumentFileTable = (cetip, isAdmin, documentService) => {
    const [state, dispatch] = useReducer(reducer, initialState)
    const folder = `emissao/${cetip}`

    useEffect(() => {
        const cols = isAdmin
            ? AdminColumn(
                  showModalDelete,
                  updateDocumentPermission,
                  downloadDocument
              )
            : commonColumn(downloadDocument)
        dispatch({ type: "SET_COLUMNS", payload: cols })
    }, [isAdmin, cetip])

    const loadTable = async () => {
        dispatch({ type: "SET_LOADING", payload: true })

        try {
            const response = await documentService.getFilesFromDirectoryFolder(
                DOCUMENTOS_BUCKET,
                folder
            )
            const {
                body: { data },
            } = response
            const list = data.map((obj, index) => ({ ...obj, id: index }))
            dispatch({ type: "SET_ROWS", payload: list })
        } catch (e) {
            dispatch({ type: "SET_ERROR", payload: e })
        } finally {
            dispatch({ type: "SET_LOADING", payload: false })
        }
    }

    const addDocument = async (accessType, filename, fileBuffer) => {
        dispatch({ type: "SET_LOADING", payload: true })

        try {
            await documentService.registerDocument(
                DOCUMENTOS_BUCKET,
                folder,
                accessType,
                filename,
                fileBuffer
            )
        } catch (e) {
            console.error("error", e)
        } finally {
            dispatch({ type: "SET_LOADING", payload: false })
        }
    }

    const downloadDocument = async (id, name) => {
        dispatch({ type: "SET_LOADING", payload: true })

        try {
            const res = await documentService.openFileFromDirectory(
                id,
                DOCUMENTOS_BUCKET,
                folder,
                name
            )

            const blob = res instanceof Blob ? res : res?.data || res?.body
            if (!(blob instanceof Blob)) {
                throw new Error("Response não é um Blob")
            }
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement("a")
            a.href = url
            a.download = name
            document.body.appendChild(a)
            a.click()
            a.remove()
            window.URL.revokeObjectURL(url)
        } catch (e) {
            console.error("error", e)
        } finally {
            dispatch({ type: "SET_LOADING", payload: false })
        }
    }

    const showModalDelete = (row) => {
        dispatch({
            type: "SET_MODAL_DELETE",
            payload: {
                isOpen: true,
                onConfirm: async () => {
                    await deleteDocument(row)
                    dispatch({ type: "RESET_MODAL_DELETE" })
                },
                onExit: () => {
                    dispatch({ type: "RESET_MODAL_DELETE" })
                },
            },
        })
    }

    const toggleAccess = (current) =>
        current === "PUBLIC" ? "PRIVATE" : "PUBLIC"

    const updateDocumentPermission = async (row, accessActual) => {
        const newAccess = toggleAccess(accessActual)

        try {
            await documentService.updateDocumentTypeAccess(row, newAccess)
            await loadTable()
        } catch (e) {
            console.log(e)
        }
    }

    const deleteDocument = async (row) => {
        dispatch({ type: "SET_LOADING", payload: true })
        try {
            await documentService.deleteFile(DOCUMENTOS_BUCKET, folder, row)
            await loadTable()
        } catch (e) {
            console.log(e)
        } finally {
            dispatch({ type: "SET_LOADING", payload: false })
        }
    }

    return {
        state,
        actions: {
            loadTable,
            addDocument,
            downloadDocument,
        },
    }
}
